import React, { Suspense, lazy, useEffect, useCallback } from "react";
import { Box, Skeleton } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useUser } from "../../context/LoginDetail";
import { useModal } from "../../context/ModalContext";
import { get_profile } from "../../api/ControllApi";
import ReferralModal from "../../components/common/ReferralModal";
import { Helmet } from "react-helmet-async";

// ── Above-the-fold: import eagerly ──────────────────────────────────────────
import Herobanner from "./Herobanner";

// ── Below-the-fold: lazy-load so they are NOT in the initial JS bundle ──────
const FeaturedPlots = lazy(() => import("./FeaturedPlots"));
const BannerSlider = lazy(() => import("./Sliderbanner"));
const ServiceCategory = lazy(() => import("./ServiceCategory"));
const ServicesMaterialBanner = lazy(() => import("./ServicesMaterialBanner"));
const GiooPlots = lazy(() => import("./GiooPlots"));
const SyndicatePlotsHome = lazy(() => import("./SyndicatePlotsHome"));
const RentalHomeSlider = lazy(() => import("./RentalHomeSlider"));
const ResidentialHomeSlider = lazy(() => import("./ResidentialHomeSlider"));
const BestSelling = lazy(() => import("./BestSelling"));
const TopFooter = lazy(() => import("../../components/footer/Topfooter"));

// ── Lightweight placeholder shown while a lazy chunk is downloading ──────────
const SectionSkeleton = ({ height = 300 }) => (
  <Box sx={{ px: 2, py: 2 }}>
    <Skeleton
      variant="rectangular"
      width="100%"
      height={height}
      animation="wave"
      sx={{ borderRadius: 2 }}
    />
  </Box>
);

const Home = () => {
  const { user, loading, loadUser } = useUser();
  const { showReferral, setShowReferral } = useModal();
  const location = useLocation();

  const isLoggedIn = !!user;

  useEffect(() => {
    // 1. Page Restriction: Only check on / or /home
    const isHomePage = location.pathname === "/" || location.pathname === "/home";
    
    if (isLoggedIn && isHomePage) {
      // 2. API Call — Fetch Profile on Page Load (to ensure latest reference_id)
      get_profile()
        .then((res) => {
          // 3. Popup Condition: reference_id === 1
          // Extract refId safely from common response patterns
          const data = res?.data || res;
          const refId = data?.jsondata?.reference_id || data?.reference_id;
          
          if (Number(refId) === 1) {
            setShowReferral(true);
          } else {
            setShowReferral(false);
          }
        })
        .catch((err) => {
          // 4. Loading & Error States: Fail silently
          setShowReferral(false);
        });
    } else if (!loading) {
      // Not on home or definitely not logged in -> ensure popup is hidden
      setShowReferral(false);
    }
  }, [isLoggedIn, user, loading, location.pathname, setShowReferral]);


  const isVendor = Number(user?.is_vendor) === 1;
  const isService = Number(user?.is_services) === 1;

  let showServiceBanner = true;
  let showMaterialBanner = true;

  if (isVendor && !isService) {
    showServiceBanner = false;
  } else if (isService && !isVendor) {
    showMaterialBanner = false;
  }

  return (
    <div style={{ overflowX: "hidden" }}>

      {/* ── SEO: page-level meta & structured data ── */}
      <Helmet>
        <title>Geo Rental Farms | Land, Plots &amp; Professional Services</title>
        <meta
          name="description"
          content="Geo Rental Farms — buy agricultural land, gioo nano plots, syndicate plots, rental-yield properties and hire professional services across India."
        />
        <link rel="canonical" href="https://georentalfarms.vrikshatech.in/" />
        <meta name="robots" content="index, follow" />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Geo Rental Farms | Land, Plots &amp; Professional Services" />
        <meta
          property="og:description"
          content="Buy agricultural land, gioo nano plots, syndicate plots, rental-yield properties and hire professional services across India."
        />
        <meta property="og:url" content="https://georentalfarms.vrikshatech.in/" />
        <meta property="og:image" content="https://georentalfarms.vrikshatech.in/assets/images/logo.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Geo Rental Farms | Land, Plots &amp; Professional Services" />
        <meta
          name="twitter:description"
          content="Buy agricultural land, gioo nano plots, syndicate plots, rental-yield properties and hire professional services across India."
        />
        <meta name="twitter:image" content="https://georentalfarms.vrikshatech.in/assets/images/logo.png" />

        {/* Organization JSON-LD */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Geo Rental Farms",
            "url": "https://georentalfarms.vrikshatech.in",
            "logo": "https://georentalfarms.vrikshatech.in/assets/images/logo.png",
            "description": "Buy agricultural land, plots, and professional services across India.",
            "sameAs": []
          }
        `}</script>

        {/* WebPage JSON-LD */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Geo Rental Farms | Land, Plots & Professional Services",
            "url": "https://georentalfarms.vrikshatech.in/",
            "description": "Geo Rental Farms — buy agricultural land, gioo nano plots, syndicate plots, rental-yield properties and hire professional services across India.",
            "isPartOf": {
              "@type": "WebSite",
              "name": "Geo Rental Farms",
              "url": "https://georentalfarms.vrikshatech.in"
            }
          }
        `}</script>
      </Helmet>

      {/* ── ABOVE THE FOLD – no Suspense needed ── */}
      <Herobanner />

      {/* ── BELOW THE FOLD – each wrapped individually so they stream in ── */}
      <Suspense fallback={<SectionSkeleton height={320}  />}>
        <FeaturedPlots />
      </Suspense>

      <Suspense fallback={<SectionSkeleton height={260} />}>
        <BannerSlider />
      </Suspense>



      
      <Suspense fallback={<SectionSkeleton height={380} />}>
        <RentalHomeSlider />
      </Suspense>

      <Suspense fallback={<SectionSkeleton height={380} />}>
        <GiooPlots />
      </Suspense>

      <Suspense fallback={<SectionSkeleton height={380} />}>
        <SyndicatePlotsHome />
      </Suspense>


      <Suspense fallback={<SectionSkeleton height={380} />}>
        <ResidentialHomeSlider />
      </Suspense>

      <Suspense fallback={<SectionSkeleton height={160} />}>
        <ServiceCategory />
      </Suspense>

      {showServiceBanner && (
        <Suspense fallback={<SectionSkeleton height={200} />}>
          <ServicesMaterialBanner type="services" />
        </Suspense>
      )}

      <Suspense fallback={<SectionSkeleton height={320} />}>
        <BestSelling />
      </Suspense>

      {showMaterialBanner && (
        <Suspense fallback={<SectionSkeleton height={200} />}>
          <ServicesMaterialBanner type="material" />
        </Suspense>
      )}

      <Suspense fallback={<SectionSkeleton height={120} />}>
        <TopFooter />
      </Suspense>

      {/* Referral Popup - only renders on Home page */}
      <ReferralModal 
        open={showReferral} 
        handleClose={() => setShowReferral(false)} 
      />
    </div>
  );
};

export default Home;
